import { useEffect, useState, useRef } from 'react';
import parse from 'html-react-parser';
import { Modal } from 'react-bootstrap';

import { TypeService } from '../../services/types/TypeService';

import { Type } from '../../classess/type/type';
import {
    isUndNullEmp, getResponse, handleResponseMessageAsync, settings, styles, getProcessButtons
} from '../../helpers/main';

const { icons, images, labels, messages, } = settings;

export default function TypeAddEditDetails({
    modalObj = {
        $: {},
        type: 0,
        auth: {},
        showModal: false,
        languageId: 'en',
        dtTypeId: '',
        dtTypeRowId: 0,
    },
    setModalObj = {},
    type = Type.class({}, false),
    setType = {},
}) {
    //#region variables
    const [process, setProcess] = useState({ save: false });
    const [nameError, setNameError] = useState('');
    const [dataFormLength] = useState(Type.minMaxLength(false));
    const isMount = useRef(true);
    const controls = {
        buttons: {
            back: {
                defaultClass: 'btn btn-outline-secondary shadow-sm font-weight-bold mx-1 px-4',
                id: 'btnBack',
            },
            save: {
                defaultClass: 'btn btn-primary shadow-sm font-weight-bold mx-1 px-4',
                id: 'btnSave',
            },
            closeModal: {
                defaultClass: 'close border-0 bg-transparent',
                id: 'btnModalClose',
            },
        },
        icons: {
            save: {
                id: 'iconSave',
                defaultClass: icons?.fasFa?.save,
            },
            back: {
                id: 'iconBack',
                defaultClass: icons?.fasFa?.arrowCircleLeft,
            },
        },
        inputs: {
            secondaryId: {
                id: 'inputSecondaryId',
            },
            name: {
                id: 'inputName',
            },
        },
        spiners: {
            save: {
                id: 'spSave',
                defaultClass: '',
            }
        },
    };
    //#endregion variables

    //#region methods
    const addType = async ({
        e = undefined,
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'addType';
        try {
            e?.preventDefault?.();

            response = setErrorsDefault({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            response = validateFormData({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            setProcess(prev => ({ ...prev, save: true }));

            response = await TypeService.create({
                auth: modalObj?.auth,
                type,
                languageId: modalObj?.languageId,
                subMethod: currentMethod
            });

            if (!response?.isSuccess || response?.isWarning) return;

            const newType = Type.class(response?.result.type, false);

            modalObj.$(`#${modalObj.dtTypeId}`).DataTable().row.add([
                newType.name,
                getProcessButtons({
                    id: newType.id,
                    description: newType.name,
                    showDetails: false, // Standardized
                    labelDetails: labels?.details[modalObj?.languageId],
                    enabledDetails: true,
                    showDelete: true,
                    labelDelete: labels?.delete[modalObj?.languageId],
                    enabledDelete: true,
                    showEdit: true,
                    labelEdit: labels?.edit?.[modalObj?.languageId],
                    enabledEdit: true,
                })?.result,
            ])?.draw(true);

            response.msgText = `${messages?.processCompletedSuccessfully?.[modalObj?.languageId]}.`;

        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        } finally {
            if (!isMount?.current) return;
            setProcess(prev => ({ ...prev, save: false }));

            if (response?.isSuccess && !response?.isWarning) {
                setDefaultValue({ closeModal: true, subMethod: currentMethod });
            }

            if (!response?.isSuccess || response?.isWarning || response?.msgText !== '' || response?.msgTextHtml !== '') {
                await handleResponseMessageAsync({
                    response,
                    currentMethod,
                    auth: modalObj?.auth,
                    languageId: modalObj?.languageId
                });
            }
        }
    }

    const editType = async ({
        e = undefined,
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'editType';
        try {
            e?.preventDefault?.();

            response = setErrorsDefault({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            response = validateFormData({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            setProcess(prev => ({ ...prev, save: true }));

            response = await TypeService.update({
                auth: modalObj?.auth,
                type,
                languageId: modalObj?.languageId,
                subMethod: currentMethod
            });

            if (!response?.isSuccess || response?.isWarning) return;

            const updatedType = Type.class(response?.result?.type, false);

            modalObj.$(`#${modalObj.dtTypeId}`).DataTable().row(modalObj.dtTypeRowId).data([
                updatedType.name,
                getProcessButtons({
                    id: updatedType.id,
                    description: updatedType.name,
                    showDetails: false,
                    labelDetails: labels?.details[modalObj?.languageId],
                    enabledDetails: true,
                    showDelete: true,
                    labelDelete: labels?.delete[modalObj?.languageId],
                    enabledDelete: true,
                    showEdit: true,
                    labelEdit: labels?.edit?.[modalObj?.languageId],
                    enabledEdit: true,
                })?.result,
            ])?.draw(true);

            response.msgText = `${messages?.processCompletedSuccessfully?.[modalObj?.languageId]}.`;

        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        } finally {
            if (!isMount?.current) return;
            setProcess(prev => ({ ...prev, save: false }));

            if (response?.isSuccess && !response?.isWarning) {
                setDefaultValue({ closeModal: true, subMethod: currentMethod });
            }

            if (!response?.isSuccess || response?.isWarning || response?.msgText !== '' || response?.msgTextHtml !== '') {
                await handleResponseMessageAsync({
                    response,
                    currentMethod,
                    auth: modalObj?.auth,
                    languageId: modalObj?.languageId
                });
            }
        }
    }

    const setDefaultValue = ({
        closeModal = false,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'setDefaultValue';
        let response = getResponse({ type: 0 });
        try {
            if (closeModal) {
                setModalObj(prev => ({
                    ...prev,
                    showModal: false,
                    type: 0,
                }));
            }
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
            console.error(response?.msgText);
        }
    }

    const validateFormData = ({
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'validateFormData';
        try {
            if (isUndNullEmp({ data: type?.name, subMethod: currentMethod })?.result) {
                setNameError(`${messages?.thisFieldRequired?.[modalObj?.languageId]}.`);
                response.isWarning = true;
            }
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    const setErrorsDefault = ({
        subMethod = '',
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'setErrorsDefault';
        try {
            setNameError(``);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    useEffect(() => {
        isMount.current = true;
        return () => {
            isMount.current = false;
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    //#endregion methods

    return (
        <Modal backdrop='static' show={modalObj?.showModal} centered style={{ borderRadius: '10px' }}>
            <Modal.Header className='border-bottom-0 pt-4 px-4 bg-white'>
                <Modal.Title className='d-flex align-items-center w-100'>
                    <div className='bg-light p-2 rounded-circle mr-3 shadow-sm'
                        style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img alt='types' src={images?.icons?.types} style={{ ...styles?.cardHeaderLogo }} />
                    </div>
                    <div className='d-flex flex-column'>
                        <span className='h5 mb-0 font-weight-bold text-dark'>{labels?.type?.[modalObj?.languageId]}</span>
                        <div className='d-flex align-items-center mt-1'>
                            <span className={`badge badge-pill py-1 px-2 text-uppercase ${modalObj?.type === 0 ? 'badge-info' : modalObj?.type === 1 ? 'badge-warning text-dark' : 'badge-secondary'}`} style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                                {modalObj?.type === 0 ? labels?.add?.[modalObj?.languageId] :
                                    modalObj?.type === 1 ? labels?.edit?.[modalObj?.languageId] :
                                        labels?.detail?.[modalObj?.languageId]}
                            </span>
                        </div>
                    </div>
                </Modal.Title>
                <button id={controls?.buttons?.closeModal?.id}
                    type='button'
                    className={controls?.buttons?.closeModal?.defaultClass}
                    data-dismiss='modal'
                    aria-label='Close'
                    disabled={process?.save}
                    style={{ fontSize: '1.5rem', marginTop: '-15px' }}
                    onClick={() => setDefaultValue({
                        closeModal: true,
                        subMethod: controls?.buttons?.closeModal?.id
                    })}>
                    <span aria-hidden='true'>&times;</span>
                </button>
            </Modal.Header>
            <form onSubmit={(e) => modalObj?.type === 0 ? addType({ e: e }) : editType({ e: e })}>
                <Modal.Body className='p-0'>
                    <div className='card-body p-4'>
                        {modalObj?.type !== 2 ? (
                            <div className='row'>
                                {/* Name */}
                                <div className='col-md-12 mb-3'>
                                    <div className='form-group'>
                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                            <i className={icons?.fasFa?.spellCheck} /> {labels?.name?.[modalObj?.languageId]}
                                        </small>
                                        <input autoFocus
                                            id={controls?.inputs?.name?.id}
                                            className='form-control bg-light border-0 shadow-sm'
                                            style={{ borderRadius: '5px' }}
                                            disabled={process?.save}
                                            maxLength={dataFormLength?.max?.name}
                                            onChange={(e) => setType({ ...type, name: e?.target?.value })}
                                            onFocus={() => setNameError('')}
                                            value={type?.name || ''}
                                            placeholder={labels?.name?.[modalObj?.languageId]}
                                            title={labels?.name?.[modalObj?.languageId]} />
                                        <div className='form-text text-danger' style={styles?.inputMsgAlert}>{parse(nameError)}</div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className='row'>
                                {/* Details View */}
                                <div className='col-md-12 mb-3'>
                                    <div className='p-3 rounded bg-light border-left border-primary shadow-sm h-100' style={{ borderLeftWidth: '5px' }}>
                                        <small className='text-muted text-uppercase font-weight-bold d-block mb-1' style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                            <i className={icons?.fasFa?.spellCheck} /> {labels?.name?.[modalObj?.languageId]}
                                        </small>
                                        <div className='h5 mb-0 font-weight-bold text-dark' style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                            {type?.name || '---'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className='border-top-0 pb-4 px-4 bg-white'>
                    <div className='d-flex justify-content-center w-100'>
                        <button id={controls?.buttons?.save?.id}
                            className={controls?.buttons?.save?.defaultClass}
                            style={{ borderRadius: '5px' }}
                            disabled={process?.save}
                            hidden={modalObj?.type === 2}
                            title={modalObj?.type === 0 ? labels?.add?.[modalObj?.languageId] : labels?.save?.[modalObj?.languageId]}
                            type='submit'>
                            {process?.save ? (
                                <span className={settings?.react?.html?.spinner?.borderSm} role='status' aria-hidden='true'></span>
                            ) : (
                                <i id={controls?.icons?.save?.id} className={`${controls?.icons?.save?.defaultClass}`} />
                            )} {modalObj?.type === 0 ? labels?.add?.[modalObj?.languageId] : labels?.save?.[modalObj?.languageId]}
                        </button>
                        <button id={controls?.buttons?.back?.id}
                            className={controls?.buttons?.back?.defaultClass}
                            style={{ borderRadius: '5px' }}
                            disabled={process?.save}
                            title={labels?.close?.[modalObj?.languageId]}
                            type='button'
                            onClick={() => setDefaultValue({ closeModal: true, subMethod: controls?.buttons?.back?.id })}>
                            <i id={controls?.icons?.back?.id} className={`${controls?.icons?.back?.defaultClass}`}></i> {labels?.back?.[modalObj?.languageId]}
                        </button>
                    </div>
                </Modal.Footer>
            </form>
        </Modal >
    )
}